'use client';

import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  type Column,
  type ColumnDef,
  type ColumnSizingState,
  type FilterFn,
  type PaginationState,
  type Row,
  type RowData,
  type Table as ReactTable,
  useReactTable,
} from '@tanstack/react-table';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Skeleton } from './Skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './Table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from './Pagination';
import { cn } from '../lib/utils';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    headClassName?: string;
    cellClassName?: string;
  }
}

type DataTablePaginationLabels = {
  navigation: string;
  rowsPerPage: string;
  page: (page: number, pageCount?: number) => string;
  previous: string;
  next: string;
  morePages: string;
};

type DataTableClientPagination = {
  type: 'client';
  initialPageSize?: number;
  pageSizeOptions?: readonly number[];
};

type DataTableCursorPagination = {
  type: 'cursor';
  pageIndex: number;
  pageSize: number;
  knownPageCount: number;
  pageSizeOptions?: readonly number[];
  canNextPage: boolean;
  pending?: boolean;
  onPageChange: (pageIndex: number) => void;
  onNextPage: () => void;
  onPageSizeChange: (pageSize: number) => void;
};

type DataTablePaginationConfig = DataTableClientPagination | DataTableCursorPagination;

type UseCursorPaginationOptions = {
  initialPageSize?: number;
};

type CursorPaginationState = {
  cursor?: string;
  pageIndex: number;
  pageSize: number;
  knownPageCount: number;
  canPreviousPage: boolean;
  goToPage: (pageIndex: number) => void;
  goToPreviousPage: () => void;
  goToNextPage: (nextCursor: string) => void;
  changePageSize: (pageSize: number) => void;
  reset: () => void;
};

function useCursorPagination({
  initialPageSize = 10,
}: UseCursorPaginationOptions = {}): CursorPaginationState {
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(initialPageSize);
  const [pageCursors, setPageCursors] = React.useState<Array<string | undefined>>([undefined]);

  const reset = React.useCallback(() => {
    setPageIndex(0);
    setPageCursors([undefined]);
  }, []);

  const goToPage = React.useCallback(
    (nextPageIndex: number) => {
      if (nextPageIndex < 0 || nextPageIndex >= pageCursors.length) return;
      setPageIndex(nextPageIndex);
    },
    [pageCursors.length],
  );

  const goToPreviousPage = React.useCallback(() => {
    setPageIndex((current) => Math.max(0, current - 1));
  }, []);

  const goToNextPage = React.useCallback(
    (nextCursor: string) => {
      const nextPageIndex = pageIndex + 1;
      setPageCursors((current) =>
        nextPageIndex < current.length ? current : [...current, nextCursor],
      );
      setPageIndex(nextPageIndex);
    },
    [pageIndex],
  );

  const changePageSize = React.useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPageIndex(0);
    setPageCursors([undefined]);
  }, []);

  return {
    cursor: pageCursors[pageIndex],
    pageIndex,
    pageSize,
    knownPageCount: pageCursors.length,
    canPreviousPage: pageIndex > 0,
    goToPage,
    goToPreviousPage,
    goToNextPage,
    changePageSize,
    reset,
  };
}

type DataTableRowProps = React.ComponentProps<typeof TableRow> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
};

type DataTableColumnLayout = {
  adaptive: boolean;
  constrained: boolean;
  fixed: boolean;
  style?: React.CSSProperties;
};

function getDataTableColumnLayout<TData, TValue>(
  column: Column<TData, TValue>,
  columnSizing: ColumnSizingState,
): DataTableColumnLayout {
  const { enableResizing, size, minSize, maxSize } = column.columnDef;
  const fixed =
    (enableResizing === false && size !== undefined) ||
    (minSize !== undefined && maxSize !== undefined && minSize === maxSize);
  const userSized = Object.prototype.hasOwnProperty.call(columnSizing, column.id);

  if (fixed || userSized) {
    const width = column.getSize();
    return {
      adaptive: false,
      constrained: true,
      fixed,
      style: { width, minWidth: width, maxWidth: width },
    };
  }

  const style =
    minSize !== undefined || maxSize !== undefined
      ? { minWidth: minSize, maxWidth: maxSize }
      : undefined;

  return {
    adaptive: true,
    constrained: maxSize !== undefined,
    fixed: false,
    style,
  };
}

type DataTableProps<TData, TValue> = {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  loading?: boolean;
  fetching?: boolean;
  error?: React.ReactNode;
  empty?: React.ReactNode;
  /** 可选筛选工具栏，渲染在表格容器内部的白色表面上（官方 Input/Select 为透明底，置于灰画布会透灰）。 */
  toolbar?: React.ReactNode;
  loadingRowCount?: number;
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  getRowProps?: (row: Row<TData>) => DataTableRowProps;
  globalFilter?: string;
  globalFilterFn?: FilterFn<TData>;
  enableColumnResizing?: boolean;
  tableClassName?: string;
  table?: ReactTable<TData>;
} & (
  | {
      pagination: DataTablePaginationConfig;
      paginationLabels: DataTablePaginationLabels;
    }
  | {
      pagination?: undefined;
      paginationLabels?: never;
    }
);

function DataTable<TData, TValue>({
  columns,
  data,
  pagination,
  paginationLabels,
  loading = false,
  fetching = false,
  error,
  empty,
  toolbar,
  loadingRowCount = 5,
  getRowId,
  getRowProps,
  globalFilter,
  globalFilterFn,
  enableColumnResizing = false,
  tableClassName,
  table: providedTable,
}: DataTableProps<TData, TValue>) {
  const [clientPagination, setClientPagination] = React.useState<PaginationState>(() => ({
    pageIndex: 0,
    pageSize:
      pagination?.type === 'client'
        ? (pagination.initialPageSize ?? 10)
        : pagination?.type === 'cursor'
          ? pagination.pageSize
          : 10,
  }));
  const isCursor = pagination?.type === 'cursor';
  const paginationState = pagination
    ? isCursor
      ? { pageIndex: pagination.pageIndex, pageSize: pagination.pageSize }
      : clientPagination
    : undefined;

  const internalTable = useReactTable({
    data,
    columns,
    getRowId,
    state: {
      ...(paginationState ? { pagination: paginationState } : {}),
      globalFilter,
    },
    onPaginationChange: pagination && !isCursor ? setClientPagination : undefined,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: globalFilterFn ? getFilteredRowModel() : undefined,
    getPaginationRowModel: pagination && !isCursor ? getPaginationRowModel() : undefined,
    globalFilterFn,
    enableColumnResizing,
    columnResizeMode: 'onChange',
    manualPagination: isCursor,
    pageCount: isCursor ? -1 : undefined,
  });
  const table = providedTable ?? internalTable;
  const columnSizing = table.getState().columnSizing;
  const visibleColumns = table.getVisibleLeafColumns();
  const hasAdaptiveColumn =
    enableColumnResizing &&
    visibleColumns.some((column) => getDataTableColumnLayout(column, columnSizing).adaptive);

  const rows = table.getRowModel().rows;
  const clientPageCount = Math.max(table.getPageCount(), 1);
  const pageCount = isCursor ? undefined : clientPageCount;
  const knownPageCount = isCursor
    ? Math.max(pagination.knownPageCount, pagination.pageIndex + 1)
    : clientPageCount;
  const canPreviousPage = isCursor ? pagination.pageIndex > 0 : table.getCanPreviousPage();
  const canNextPage = isCursor
    ? pagination.pageIndex + 1 < knownPageCount || pagination.canNextPage
    : table.getCanNextPage();
  const pending = isCursor ? pagination.pending : false;
  const pageSizeOptions = pagination?.pageSizeOptions ?? [10, 20, 50];

  function changePageSize(nextPageSize: number) {
    if (pagination?.type === 'cursor') {
      pagination.onPageSizeChange(nextPageSize);
      return;
    }
    table.setPageSize(nextPageSize);
  }

  return (
    <div className="flex min-w-0 flex-col gap-3" data-slot="data-table">
      <div className="overflow-hidden rounded-lg border bg-card" data-slot="data-table-container">
        {toolbar ? (
          <div
            className="flex flex-wrap items-center gap-2 border-b p-2.5"
            data-slot="data-table-toolbar"
          >
            {toolbar}
          </div>
        ) : null}
        <div
          className={cn(enableColumnResizing && 'overflow-x-auto')}
          data-slot="data-table-scroll"
        >
          <Table
            className={tableClassName}
            style={
              enableColumnResizing
                ? {
                    tableLayout: 'auto',
                    width: hasAdaptiveColumn ? 'max-content' : table.getTotalSize(),
                    minWidth: hasAdaptiveColumn ? '100%' : undefined,
                  }
                : undefined
            }
            aria-busy={loading || fetching}
          >
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const layout = getDataTableColumnLayout(header.column, columnSizing);
                    return (
                      <TableHead
                        key={header.id}
                        className={cn(
                          enableColumnResizing && 'relative',
                          enableColumnResizing && layout.constrained && 'overflow-hidden',
                          header.column.columnDef.meta?.headClassName,
                        )}
                        style={enableColumnResizing ? layout.style : undefined}
                      >
                        {header.isPlaceholder ? null : enableColumnResizing ? (
                          <div
                            className={cn(
                              'whitespace-nowrap',
                              layout.constrained && 'min-w-0 overflow-hidden text-ellipsis',
                            )}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                        {enableColumnResizing && header.column.getCanResize() ? (
                          <div
                            aria-hidden="true"
                            data-slot="data-table-column-resizer"
                            data-resizing={header.column.getIsResizing() ? 'true' : undefined}
                            className={cn(
                              'absolute inset-y-0 -right-1 z-10 w-2 cursor-col-resize touch-none select-none',
                              'after:absolute after:inset-y-2 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-border',
                              'hover:after:bg-foreground/50 data-[resizing=true]:after:bg-primary',
                            )}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                          />
                        ) : null}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {loading && data.length === 0 ? (
                Array.from({ length: loadingRowCount }, (_, rowIndex) => (
                  <TableRow key={`loading-${rowIndex}`}>
                    {visibleColumns.map((column, columnIndex) => {
                      const layout = getDataTableColumnLayout(column, columnSizing);
                      return (
                        <TableCell
                          key={column.id ?? `loading-${columnIndex}`}
                          style={enableColumnResizing ? layout.style : undefined}
                        >
                          <Skeleton className={cn('h-4', columnIndex === 0 ? 'w-32' : 'w-20')} />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : error && data.length === 0 ? (
                <DataTableStateRow colSpan={table.getVisibleLeafColumns().length}>
                  {error}
                </DataTableStateRow>
              ) : rows.length === 0 ? (
                <DataTableStateRow colSpan={table.getVisibleLeafColumns().length}>
                  {empty}
                </DataTableStateRow>
              ) : (
                rows.map((row) => {
                  const rowProps = getRowProps?.(row);
                  return (
                    <TableRow key={row.id} {...rowProps}>
                      {row.getVisibleCells().map((cell) => {
                        const layout = getDataTableColumnLayout(cell.column, columnSizing);
                        return (
                          <TableCell
                            key={cell.id}
                            className={cn(
                              enableColumnResizing && layout.constrained && 'overflow-hidden',
                              cell.column.columnDef.meta?.cellClassName,
                            )}
                            style={enableColumnResizing ? layout.style : undefined}
                          >
                            {enableColumnResizing ? (
                              <div
                                className={cn(
                                  'whitespace-nowrap',
                                  layout.constrained && 'min-w-0 overflow-hidden text-ellipsis',
                                )}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </div>
                            ) : (
                              flexRender(cell.column.columnDef.cell, cell.getContext())
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {pagination && paginationLabels && paginationState ? (
        <DataTablePagination
          pageIndex={paginationState.pageIndex}
          pageSize={paginationState.pageSize}
          pageSizeOptions={pageSizeOptions}
          pageCount={pageCount}
          knownPageCount={knownPageCount}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pending={pending || loading || fetching}
          labels={paginationLabels}
          onPageSizeChange={changePageSize}
          onPageChange={(nextPageIndex) => {
            if (pagination.type === 'cursor') {
              if (nextPageIndex < pagination.knownPageCount) pagination.onPageChange(nextPageIndex);
              else pagination.onNextPage();
              return;
            }
            table.setPageIndex(nextPageIndex);
          }}
        />
      ) : null}
    </div>
  );
}

type DataTablePaginationProps = {
  pageIndex: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  pageCount?: number;
  knownPageCount: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  pending?: boolean;
  labels: DataTablePaginationLabels;
  onPageSizeChange: (pageSize: number) => void;
  onPageChange: (pageIndex: number) => void;
};

function DataTablePagination({
  pageIndex,
  pageSize,
  pageSizeOptions,
  pageCount,
  knownPageCount,
  canPreviousPage,
  canNextPage,
  pending = false,
  labels,
  onPageSizeChange,
  onPageChange,
}: DataTablePaginationProps) {
  const pageItems = paginationItems(pageIndex + 1, knownPageCount);
  const previousDisabled = !canPreviousPage || pending;
  const nextDisabled = !canNextPage || pending;

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3"
      data-slot="data-table-pagination"
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>{labels.rowsPerPage}</span>
        <Select value={String(pageSize)} onValueChange={(value) => onPageSizeChange(Number(value))}>
          <SelectTrigger size="sm" aria-label={labels.rowsPerPage} disabled={pending}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="start">
            {pageSizeOptions.map((option) => (
              <SelectItem key={option} value={String(option)}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Pagination aria-label={labels.navigation} className="mx-0 w-auto justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              href="#"
              text={labels.previous}
              aria-label={labels.previous}
              aria-disabled={previousDisabled}
              tabIndex={previousDisabled ? -1 : undefined}
              className={cn(previousDisabled && 'pointer-events-none opacity-50')}
              onClick={(event) => {
                event.preventDefault();
                if (!previousDisabled) onPageChange(pageIndex - 1);
              }}
            />
          </PaginationItem>
          {pageItems.map((item) =>
            typeof item === 'number' ? (
              <PaginationItem key={item}>
                <PaginationLink
                  href="#"
                  isActive={item === pageIndex + 1}
                  aria-label={labels.page(item, pageCount)}
                  aria-disabled={pending}
                  tabIndex={pending ? -1 : undefined}
                  className={cn(pending && 'pointer-events-none opacity-50')}
                  onClick={(event) => {
                    event.preventDefault();
                    if (!pending && item !== pageIndex + 1) onPageChange(item - 1);
                  }}
                >
                  {item}
                </PaginationLink>
              </PaginationItem>
            ) : (
              <PaginationItem key={item}>
                <PaginationEllipsis label={labels.morePages} />
              </PaginationItem>
            ),
          )}
          <PaginationItem>
            <PaginationNext
              href="#"
              text={labels.next}
              aria-label={labels.next}
              aria-disabled={nextDisabled}
              tabIndex={nextDisabled ? -1 : undefined}
              className={cn(nextDisabled && 'pointer-events-none opacity-50')}
              onClick={(event) => {
                event.preventDefault();
                if (!nextDisabled) onPageChange(pageIndex + 1);
              }}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  );
}

type PaginationItemValue = number | 'ellipsis-start' | 'ellipsis-end';

function paginationItems(currentPage: number, pageCount: number): PaginationItemValue[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1);

  const pages = new Set([1, pageCount, currentPage - 1, currentPage, currentPage + 1]);
  const visiblePages = [...pages]
    .filter((page) => page >= 1 && page <= pageCount)
    .sort((left, right) => left - right);
  const items: PaginationItemValue[] = [];

  for (const page of visiblePages) {
    const previous = items.at(-1);
    const previousPage = typeof previous === 'number' ? previous : undefined;
    if (previousPage !== undefined && page - previousPage > 1) {
      items.push(previousPage === 1 ? 'ellipsis-start' : 'ellipsis-end');
    }
    items.push(page);
  }

  return items;
}

function DataTableStateRow({ colSpan, children }: { colSpan: number; children: React.ReactNode }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="h-24 text-center text-muted-foreground">
        {children}
      </TableCell>
    </TableRow>
  );
}

export {
  DataTable,
  DataTablePagination,
  useCursorPagination,
  type CursorPaginationState,
  type DataTableClientPagination,
  type DataTableCursorPagination,
  type DataTablePaginationConfig,
  type DataTablePaginationLabels,
  type DataTablePaginationProps,
  type DataTableProps,
  type UseCursorPaginationOptions,
};
